import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CASE_STUDIES } from "@/data/site";

const FeaturedCase = () => {
  // Using hospitality case as the featured real story
  const featuredCase = CASE_STUDIES.find(study => study.id === 'hospitality-audit-dashboards');

  if (!featuredCase) {
    return null;
  }

  return (
    <section className="border-y border-border bg-card/30">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
        
        <Reveal>
          <div className="mb-8">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">
              Featured case
            </span>
          </div>
        </Reveal>

        <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:items-center">
          
          {/* Left side - Case info */}
          <div>
            <Reveal delay={0.1}>
              {/* Industry chip */}
              <div className="mb-6">
                <div className="inline-flex items-center justify-center rounded-lg border border-border bg-background px-4 py-2 font-mono text-sm font-medium text-foreground">
                  {featuredCase.industry}
                </div>
              </div>

              {/* Title and outcome */}
              <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                {featuredCase.title}
              </h2>
              <p className="mt-4 text-xl text-muted-foreground">
                {featuredCase.subtitle}
              </p>
            </Reveal>

            {/* Outcome - no metrics shown */}
            <Reveal delay={0.2}>
              <div className="mt-8">
                <p className="text-base text-muted-foreground">
                  Production React on a live multi-tenant stack — giving hotels clear insight into their performance against industry standards.
                </p>
              </div>
            </Reveal>

            {/* CTA */}
            <Reveal delay={0.3}>
              <div className="mt-10">
                <Link
                  to={`/case-study/${featuredCase.id}`}
                  className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-coral transition-colors duration-300 hover:text-coral/80"
                >
                  Read case study
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                
                <div className="mt-4">
                  <Link
                    to="/case-studies"
                    className="text-sm text-muted-foreground transition-colors duration-300 hover:text-foreground"
                  >
                    All case studies →
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right side - Before/After */}
          <div>
            <Reveal delay={0.25}>
              <div className="grid gap-8 sm:grid-cols-2">
                
                {/* Before */}
                <div>
                  <h3 className="mb-4 font-display text-lg font-bold tracking-tight text-destructive">
                    Before
                  </h3>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 block h-1 w-1 rounded-full bg-muted-foreground/60"></span>
                      <span>No client-facing visibility into assessments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 block h-1 w-1 rounded-full bg-muted-foreground/60"></span>
                      <span>Standing vs peers buried in reports</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 block h-1 w-1 rounded-full bg-muted-foreground/60"></span>
                      <span>No benchmarking against standards</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 block h-1 w-1 rounded-full bg-muted-foreground/60"></span>
                      <span>No day-to-day product surface for quality ops</span>
                    </li>
                  </ul>
                </div>

                {/* After */}
                <div>
                  <h3 className="mb-4 font-display text-lg font-bold tracking-tight text-coral">
                    After
                  </h3>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 block h-1 w-1 rounded-full bg-coral"></span>
                      <span>Hotel-facing dashboards with live standings</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 block h-1 w-1 rounded-full bg-coral"></span>
                      <span>Benchmarking against standards and peers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 block h-1 w-1 rounded-full bg-coral"></span>
                      <span>Day-to-day visibility into assessment outcomes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 block h-1 w-1 rounded-full bg-coral"></span>
                      <span>Multi-tenant production React on the live stack</span>
                    </li>
                  </ul>
                </div>

              </div>
            </Reveal>
          </div>

        </div>

      </div>
    </section>
  );
};

export default FeaturedCase;